import { Link, useParams } from 'react-router-dom';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import { useFavorites } from '@/hooks/useFavorites';
import { useToast } from '@/hooks/useToast';
import { usePageMeta } from '@/hooks/usePageMeta';
import Toast from '@/components/base/Toast';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';

type MetaRow = { icon: string; label: string; value: string };

export default function Detail() {
  const { id } = useParams();
  const movie = getMovieById(Number(id));
  const { isFavorite, toggleFavorite } = useFavorites();
  const { toast, showToast } = useToast();

  usePageMeta({
    title: movie ? `${movie.title} - 北条麻妃电影详情` : '电影详情 - 北条麻妃电影',
    description: movie
      ? `${movie.title}（${movie.year}）：${movie.synopsis.slice(0, 60)}……`
      : '北条麻妃电影详情页，展示剧情介绍、导演、上映年份等详细资料。',
    keywords: movie
      ? `${movie.title},北条麻妃电影,${movie.genres.join(',')},电影剧情介绍`
      : '北条麻妃电影详情,剧情介绍',
  });

  if (!movie) {
    return (
      <div className="mx-auto flex w-full max-w-6xl flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-file-unknow-line text-2xl"></i>
        </span>
        <h1 className="mt-5 text-xl font-bold text-foreground-950">暂无相关资料</h1>
        <p className="mt-2 max-w-md text-sm text-foreground-500">
          很抱歉，我们没有找到这部作品的信息，它可能已被移除或链接有误。
        </p>
        <Link
          to="/category"
          className="mt-6 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
        >
          返回电影分类
        </Link>
      </div>
    );
  }

  const favorite = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 4);

  const metaRows: MetaRow[] = [
    { icon: 'ri-user-star-line', label: '导演', value: movie.director },
    { icon: 'ri-calendar-line', label: '上映年份', value: String(movie.year) },
    { icon: 'ri-calendar-check-line', label: '上映日期', value: movie.releaseDate },
    { icon: 'ri-time-line', label: '片长', value: `${movie.runtime} 分钟` },
    { icon: 'ri-translate-2', label: '语言', value: movie.language },
    { icon: 'ri-map-pin-line', label: '制片地区', value: movie.country },
    { icon: 'ri-movie-line', label: '类型', value: movie.genres.join(' / ') },
    { icon: 'ri-star-smile-line', label: '评分', value: movie.rating.toFixed(1) },
  ];

  const handleFavorite = () => {
    const added = toggleFavorite(movie.id);
    showToast(added ? '已收藏，可在收藏列表查看' : '已取消收藏', added ? 'accent' : 'default');
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast('链接已复制到剪贴板');
    } catch {
      showToast('复制失败，请手动复制地址栏链接');
    }
  };

  return (
    <>
      <section className="relative overflow-hidden border-b border-background-200 bg-background-100/70">
        <div className="absolute inset-0">
          <img
            src={movie.poster}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-top blur-2xl opacity-40"
          />
        </div>
        <div className="absolute inset-0 bg-background-100/60"></div>

        <div className="relative z-10 mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '电影分类', to: '/category' },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 flex flex-col gap-6 md:flex-row md:gap-8">
            <div className="mx-auto w-44 shrink-0 overflow-hidden rounded-lg border border-background-200 bg-background-200 sm:w-52 md:mx-0 md:w-60">
              <img
                src={movie.poster}
                alt={`${movie.title} 电影海报`}
                title={`${movie.title} - 北条麻妃电影`}
                className="aspect-[2/3] h-full w-full object-top"
              />
            </div>

            <div className="min-w-0 flex-1 text-center md:text-left">
              <div className="flex flex-wrap justify-center gap-2 md:justify-start">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {g}
                  </Link>
                ))}
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
                  {movie.year}
                </span>
              </div>

              <h1 className="mt-4 text-2xl font-bold text-foreground-950 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {movie.tagline}
              </p>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-3 md:justify-start">
                <span className="flex items-center gap-1.5 rounded-md bg-background-50 px-3 py-2 text-sm font-semibold text-accent-700">
                  <i className="ri-star-fill"></i>
                  {movie.rating.toFixed(1)}
                  <span className="font-normal text-foreground-400">/ 10</span>
                </span>
                <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                  <i className="ri-user-star-line"></i>
                  {movie.director} 执导
                </span>
                <span className="flex items-center gap-1.5 text-sm text-foreground-600">
                  <i className="ri-time-line"></i>
                  {movie.runtime} 分钟
                </span>
              </div>

              <div className="mt-6 flex flex-wrap justify-center gap-3 md:justify-start">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center gap-2 rounded-md px-5 py-2.5 text-sm font-semibold transition-colors whitespace-nowrap ${
                    favorite
                      ? 'bg-accent-500 text-background-50 hover:bg-accent-600'
                      : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                  }`}
                >
                  <i className={favorite ? 'ri-heart-3-fill text-base' : 'ri-heart-3-line text-base'}></i>
                  {favorite ? '已收藏' : '收藏该作品'}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap"
                >
                  <i className="ri-link text-base"></i>
                  复制链接
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-12">
        <div className="flex flex-col gap-10 lg:flex-row lg:gap-12">
          <div className="min-w-0 flex-1">
            <article>
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                剧情简介
              </h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                {movie.synopsis}
              </p>
            </article>

            <div className="mt-10">
              <h2 className="flex items-center gap-2 text-lg font-bold text-foreground-950 md:text-xl">
                <span className="h-4 w-1 rounded-full bg-accent-500"></span>
                主要演员
              </h2>
              <div className="mt-4 flex flex-wrap gap-2.5">
                {movie.cast.map((name) => (
                  <span
                    key={name}
                    className={`rounded-full border px-4 py-2 text-sm ${
                      name === '北条麻妃'
                        ? 'border-accent-300 bg-accent-100 font-semibold text-accent-800'
                        : 'border-background-300 bg-background-50 text-foreground-700'
                    }`}
                  >
                    {name}
                    {name === '北条麻妃' && <span className="ml-1 text-xs">主演</span>}
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-10 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
              <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-price-tag-3-line text-primary-600"></i>
                相关分类标签
              </h2>
              <div className="mt-3 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category?genre=${encodeURIComponent(g)}`}
                    className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                  >
                    {g}片
                  </Link>
                ))}
                <Link
                  to={`/category?year=${movie.year}`}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  {movie.year} 年
                </Link>
                <Link
                  to={`/category?country=${encodeURIComponent(movie.country)}`}
                  className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  {movie.country}
                </Link>
              </div>
            </div>
          </div>

          <aside className="w-full lg:w-72 lg:shrink-0">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <h2 className="flex items-center gap-2 text-sm font-bold text-foreground-950">
                <i className="ri-information-line text-primary-600"></i>
                作品资料
              </h2>
              <dl className="mt-4 flex flex-col divide-y divide-background-200">
                {metaRows.map((row) => (
                  <div key={row.label} className="flex items-start gap-3 py-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                      <i className={`${row.icon} text-sm`}></i>
                    </span>
                    <div className="min-w-0">
                      <dt className="text-xs text-foreground-500">{row.label}</dt>
                      <dd className="mt-0.5 text-sm font-medium text-foreground-900">
                        {row.value || '暂无相关资料'}
                      </dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-14">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-foreground-950 md:text-xl">相关推荐</h2>
                <p className="mt-1.5 text-sm text-foreground-600">
                  与你正在查看的作品类型相近，也许同样值得一看
                </p>
              </div>
              <Link
                to={`/category?genre=${encodeURIComponent(movie.genres[0])}`}
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700 whitespace-nowrap"
              >
                查看更多{movie.genres[0]}片
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 md:gap-5">
              {related.map((m, i) => (
                <MovieCard key={m.id} movie={m} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>

      <Toast toast={toast} />
    </>
  );
}